/**
 * `/admin/compliance-obligations/:requirementId` — detail + edit surface
 * for a single compliance requirement (M13, PRD 06 §Admin configuration,
 * M10 rework). SPA URL renamed to "obligations" as an admin-facing copy
 * change; backend API path stays `/compliance-requirements`.
 *
 * Every authenticated admin can read and edit obligations (the
 * role-tier model was retired in M15).
 *
 * After M10 the schema is small: name, short_code, pattern, active,
 * markdown_explainer, linked_document_id, and expiry_duration_days.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import type { ApiClient } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import type {
  ComplianceRequirement,
  Document,
  RequirementPattern,
  UpdateComplianceRequirementRequest,
} from '@isms/shared';

export interface ComplianceRequirementDetailPageProps {
  client: ApiClient;
  signInPath?: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; error: ApiError | string }
  | { kind: 'not_found' }
  | { kind: 'ready'; item: ComplianceRequirement };

type SaveStatus =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'success'; message: string }
  | { kind: 'error'; error: ApiError | string };

interface EditFields {
  name: string;
  shortCode: string;
  pattern: RequirementPattern;
  active: boolean;
  markdownExplainer: string;
  linkedDocumentId: string;
  expiryDurationDays: string;
  confirmButtonLabel: string;
}

export function ComplianceRequirementDetailPage({
  client,
  signInPath = '/admin/sign-in',
}: ComplianceRequirementDetailPageProps): JSX.Element {
  const { requirementId } = useParams<{ requirementId: string }>();
  const navigate = useNavigate();

  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [status, setStatus] = useState<SaveStatus>({ kind: 'idle' });

  const redirectToSignIn = useCallback(() => {
    navigate(signInPath, { replace: true });
  }, [navigate, signInPath]);

  const load = useCallback(async () => {
    if (!requirementId) return;
    setState({ kind: 'loading' });
    try {
      const item = await client.adminGetComplianceRequirement(requirementId);
      setState({ kind: 'ready', item });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        redirectToSignIn();
        return;
      }
      if (err instanceof ApiError && err.status === 404) {
        setState({ kind: 'not_found' });
        return;
      }
      setState({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : "We couldn't load that obligation. Please check your connection and try again.",
      });
    }
  }, [client, redirectToSignIn, requirementId]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(
    async (patch: UpdateComplianceRequirementRequest) => {
      if (!requirementId) return;
      setStatus({ kind: 'pending' });
      try {
        const updated = await client.adminPatchComplianceRequirement(requirementId, patch);
        setState({ kind: 'ready', item: updated });
        setStatus({ kind: 'success', message: 'Changes saved.' });
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          redirectToSignIn();
          return;
        }
        setStatus({
          kind: 'error',
          error:
            err instanceof ApiError ? err : "We couldn't save those changes. Please try again.",
        });
      }
    },
    [client, redirectToSignIn, requirementId],
  );

  return (
    <main className="admin-user-detail" aria-labelledby="admin-item-type-detail-heading">
      <nav className="admin-people-breadcrumb" aria-label="Breadcrumb">
        <Link to="/admin/compliance-obligations">← Back to obligations</Link>
      </nav>

      <h1 id="admin-item-type-detail-heading">Obligation</h1>

      {state.kind === 'loading' ? (
        <p className="admin-people-loading" aria-busy="true">
          Loading requirement…
        </p>
      ) : null}

      {state.kind === 'not_found' ? (
        <div className="admin-people-error" role="alert">
          <p>We couldn't find that obligation. It may have been removed.</p>
          <Link to="/admin/compliance-obligations">Back to obligations</Link>
        </div>
      ) : null}

      {state.kind === 'error' ? (
        <div className="admin-people-error">
          <ErrorBanner error={state.error} />
          <button type="button" onClick={() => void load()}>
            Retry
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' ? (
        <>
          <RequirementHeader item={state.item} />
          <EditForm
            client={client}
            item={state.item}
            status={status}
            onSubmit={(patch) => void save(patch)}
          />
        </>
      ) : null}
    </main>
  );
}

interface RequirementHeaderProps {
  item: ComplianceRequirement;
}

function RequirementHeader({ item }: RequirementHeaderProps): JSX.Element {
  return (
    <section className="admin-user-detail-header" aria-label="Obligation summary">
      <div className="admin-user-detail-identity">
        <span className="admin-user-detail-name">{item.name}</span>
        <span className="admin-user-detail-email">
          <code>{item.short_code}</code>
        </span>
      </div>
      <div className="admin-user-detail-badges">
        <span
          className={`admin-people-status ${
            item.active ? 'admin-people-status-active' : 'admin-people-status-inactive'
          }`}
        >
          {item.active ? 'Active' : 'Inactive'}
        </span>
      </div>
      <dl className="admin-user-detail-meta">
        <dt>Created</dt>
        <dd>{formatTimestamp(item.created_at)}</dd>
        <dt>Last updated</dt>
        <dd>{formatTimestamp(item.updated_at)}</dd>
      </dl>
    </section>
  );
}

interface EditFormProps {
  client: ApiClient;
  item: ComplianceRequirement;
  status: SaveStatus;
  onSubmit: (patch: UpdateComplianceRequirementRequest) => void;
}

type DocumentsState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; documents: Document[] }
  | { kind: 'error'; message: string };

function EditForm({ client, item, status, onSubmit }: EditFormProps): JSX.Element {
  const initial = useMemo(() => toEditFields(item), [item]);
  const [fields, setFields] = useState<EditFields>(initial);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [documents, setDocuments] = useState<DocumentsState>({ kind: 'idle' });

  // Resync when the parent refetches (e.g. after save).
  useEffect(() => {
    setFields(initial);
  }, [initial]);

  // Fetch documents lazily when the form first needs them (pattern === 'sign').
  // Cancellation handles unmount + the pattern flipping back to non-sign mid-flight;
  // we deliberately don't depend on `documents.kind` because setting it inside
  // the effect would re-trigger the effect and cancel its own fetch.
  useEffect(() => {
    if (fields.pattern !== 'sign') return;
    let cancelled = false;
    setDocuments({ kind: 'loading' });
    void (async () => {
      try {
        const res = await client.adminListDocuments({ page_size: 100 });
        if (cancelled) return;
        setDocuments({ kind: 'ready', documents: res.items });
      } catch (err) {
        if (cancelled) return;
        setDocuments({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Failed to load documents.',
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, fields.pattern]);

  const pending = status.kind === 'pending';
  const dirty = JSON.stringify(fields) !== JSON.stringify(initial);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!dirty || pending) return;
    setValidationError(null);
    let patch: UpdateComplianceRequirementRequest;
    try {
      patch = buildPatch(fields, item);
    } catch (err) {
      setValidationError(err instanceof Error ? err.message : 'Invalid input.');
      return;
    }
    if (Object.keys(patch).length === 0) return;
    onSubmit(patch);
  }

  return (
    <section className="admin-user-detail-section" aria-labelledby="admin-item-type-edit-heading">
      <h2 id="admin-item-type-edit-heading">Configuration</h2>
      <form onSubmit={handleSubmit} noValidate className="admin-users-create-form">
        <div className="admin-users-form-row">
          <label htmlFor="itype-name">Name</label>
          <input
            id="itype-name"
            type="text"
            required
            value={fields.name}
            onChange={(e) => setFields({ ...fields, name: e.target.value })}
            disabled={pending}
          />
        </div>

        <div className="admin-users-form-row">
          <label htmlFor="itype-short-code">Short code</label>
          <input
            id="itype-short-code"
            type="text"
            required
            value={fields.shortCode}
            onChange={(e) => setFields({ ...fields, shortCode: e.target.value })}
            disabled={pending}
          />
        </div>

        <div className="admin-users-form-row">
          <label htmlFor="itype-pattern">Pattern</label>
          <select
            id="itype-pattern"
            value={fields.pattern}
            onChange={(e) => {
              const nextPattern = e.target.value as RequirementPattern;
              setFields({
                ...fields,
                pattern: nextPattern,
                // Server invariant: only `sign` rows may carry a
                // linked_document_id. Clear it on pattern change so a
                // stale id doesn't follow the row into a non-sign state.
                linkedDocumentId: nextPattern === 'sign' ? fields.linkedDocumentId : '',
              });
            }}
            disabled={pending}
          >
            <option value="upload">Upload</option>
            <option value="sign">Sign</option>
            <option value="admin_attestation">Admin attestation</option>
            <option value="volunteer_attestation">Volunteer attestation</option>
          </select>
        </div>

        <div className="admin-users-form-row admin-users-form-row-checkbox">
          <label htmlFor="itype-active">
            <input
              id="itype-active"
              type="checkbox"
              checked={fields.active}
              onChange={(e) => setFields({ ...fields, active: e.target.checked })}
              disabled={pending}
            />
            Active — future intake flows will require this
          </label>
        </div>

        <div className="admin-users-form-row">
          <label htmlFor="itype-markdown">Markdown explainer</label>
          <textarea
            id="itype-markdown"
            rows={5}
            value={fields.markdownExplainer}
            onChange={(e) => setFields({ ...fields, markdownExplainer: e.target.value })}
            disabled={pending}
          />
          <p className="admin-users-form-hint">
            Volunteer-facing copy rendered on <code>/me</code>. Empty for{' '}
            <code>admin_attestation</code> rows.
          </p>
        </div>

        {fields.pattern === 'sign' ? (
          <div className="admin-users-form-row">
            <label htmlFor="itype-linked-document">Linked document</label>
            {documents.kind === 'loading' || documents.kind === 'idle' ? (
              <select id="itype-linked-document" disabled>
                <option>Loading documents…</option>
              </select>
            ) : documents.kind === 'error' ? (
              <ErrorBanner error={documents.message} />
            ) : documents.documents.length === 0 ? (
              <p className="admin-users-form-hint">
                No policy documents yet —{' '}
                <Link to="/admin/documents">add one in the documents section</Link> before saving a
                sign-pattern obligation.
              </p>
            ) : (
              <select
                id="itype-linked-document"
                required
                value={fields.linkedDocumentId}
                onChange={(e) => setFields({ ...fields, linkedDocumentId: e.target.value })}
                disabled={pending}
              >
                <option value="">— Select a document —</option>
                {/* Preserve a stale selection that's outside the current page,
                    so an admin doesn't accidentally null it just by opening
                    the form. */}
                {fields.linkedDocumentId &&
                !documents.documents.some(
                  (d) => String(d.document_id) === fields.linkedDocumentId,
                ) ? (
                  <option value={fields.linkedDocumentId}>
                    Document #{fields.linkedDocumentId} (not in current list)
                  </option>
                ) : null}
                {documents.documents
                  .filter((d) => d.archived_at == null)
                  .map((d) => (
                    <option key={String(d.document_id)} value={String(d.document_id)}>
                      {documentLabel(d)}
                    </option>
                  ))}
              </select>
            )}
          </div>
        ) : null}

        <div className="admin-users-form-row">
          <label htmlFor="itype-expiry-duration">Expiry duration (days)</label>
          <input
            id="itype-expiry-duration"
            type="number"
            min={1}
            value={fields.expiryDurationDays}
            onChange={(e) => setFields({ ...fields, expiryDurationDays: e.target.value })}
            disabled={pending}
          />
          <p className="admin-users-form-hint">
            How long a verified record stays valid. Leave blank for ack rows or attestations whose
            cadence isn't date-driven.
          </p>
        </div>

        {fields.pattern === 'volunteer_attestation' ? (
          <div className="admin-users-form-row">
            <label htmlFor="itype-confirm-button-label">Confirm button label</label>
            <input
              id="itype-confirm-button-label"
              type="text"
              value={fields.confirmButtonLabel}
              onChange={(e) => setFields({ ...fields, confirmButtonLabel: e.target.value })}
              disabled={pending}
            />
            <p className="admin-users-form-hint">
              Button copy on <code>/me</code> and the wording quoted in the daily-reminder email.
              Defaults to <code>I confirm</code>; override with something contextual (e.g.{' '}
              <code>I&apos;ve reviewed my details</code>).
            </p>
          </div>
        ) : null}

        <div className="admin-users-create-actions">
          <button
            type="submit"
            className="admin-exports-primary-button"
            disabled={pending || !dirty}
          >
            {pending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
        {validationError ? <ErrorBanner error={validationError} /> : null}
        {status.kind === 'success' ? (
          <p className="admin-exports-action-success" role="status">
            {status.message}
          </p>
        ) : null}
        {status.kind === 'error' ? <ErrorBanner error={status.error} /> : null}
      </form>
    </section>
  );
}

function toEditFields(item: ComplianceRequirement): EditFields {
  return {
    name: item.name,
    shortCode: item.short_code,
    pattern: item.pattern,
    active: item.active,
    markdownExplainer: item.markdown_explainer,
    linkedDocumentId: item.linked_document_id === null ? '' : String(item.linked_document_id),
    expiryDurationDays: item.expiry_duration_days === null ? '' : String(item.expiry_duration_days),
    confirmButtonLabel: item.confirm_button_label,
  };
}

function buildPatch(
  fields: EditFields,
  original: ComplianceRequirement,
): UpdateComplianceRequirementRequest {
  const patch: UpdateComplianceRequirementRequest = {};

  const trimmedName = fields.name.trim();
  if (!trimmedName) throw new Error('Name is required.');
  if (trimmedName !== original.name) patch.name = trimmedName;

  const trimmedCode = fields.shortCode.trim();
  if (!trimmedCode) throw new Error('Short code is required.');
  if (trimmedCode !== original.short_code) patch.short_code = trimmedCode;

  if (fields.pattern !== original.pattern) patch.pattern = fields.pattern;
  if (fields.active !== original.active) patch.active = fields.active;

  if (fields.markdownExplainer !== original.markdown_explainer)
    patch.markdown_explainer = fields.markdownExplainer;

  const linkedDocumentId = parseOptionalIdString(
    fields.linkedDocumentId,
    'Linked document id must be a non-negative integer or blank.',
  );
  const originalLinkedString =
    original.linked_document_id === null ? null : String(original.linked_document_id);
  if (linkedDocumentId !== originalLinkedString) {
    patch.linked_document_id = linkedDocumentId;
  }

  const expiryDuration = parseOptionalPositiveInt(
    fields.expiryDurationDays,
    'Expiry duration must be a positive whole number or blank.',
  );
  if (expiryDuration !== original.expiry_duration_days) patch.expiry_duration_days = expiryDuration;

  const trimmedButtonLabel = fields.confirmButtonLabel.trim();
  if (!trimmedButtonLabel) throw new Error('Confirm button label cannot be empty.');
  if (trimmedButtonLabel !== original.confirm_button_label) {
    patch.confirm_button_label = trimmedButtonLabel;
  }

  return patch;
}

function parseOptionalPositiveInt(raw: string, message: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) throw new Error(message);
  const n = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(n) || n < 1) throw new Error(message);
  return n;
}

function parseOptionalIdString(raw: string, message: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) throw new Error(message);
  return trimmed;
}

function formatTimestamp(v: string | Date): string {
  const s = typeof v === 'string' ? v : v.toISOString();
  return s.replace('T', ' ').replace(/\..*$/, '').slice(0, 16);
}

function documentLabel(d: Document): string {
  const head = `${d.title} (v${d.version})`;
  return d.archived_at != null ? `${head} — archived` : head;
}
